// src/components/covers/CoverCardModal.jsx
import React, { useEffect, useCallback, useState } from "react";
import { API_BASE } from "../../utils/api";
import "../../styles/CoverCardModal.css";

const formatPrice = (cents, currency = "usd") => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
};

export default function CoverCardModal({
  open,
  cover,
  onClose,
  context = "coverMarket", // "coverMarket" | "myCovers"
}) {
  const [owned, setOwned] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // For assigning cover to a draft
  const [drafts, setDrafts] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const accessToken = localStorage.getItem("rv_access_token");

  const escHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", escHandler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", escHandler);
      document.body.style.overflow = prev;
    };
  }, [open, escHandler]);

  const id = cover?._id || cover?.id;

  // Ownership check
  useEffect(() => {
    let alive = true;
    if (!open || !id) return;

    const check = async () => {
      if (context === "myCovers") {
        setOwned(true);
        setChecking(false);
        return;
      }
      try {
        setChecking(true);
        const headers = accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : {};
        const res = await fetch(`${API_BASE}/api/cover-market/ownership/${id}`, {
          credentials: "omit",
          headers,
        });
        const data = await res.json().catch(() => ({}));
        if (alive) setOwned(!!data.owned);
      } catch {
        if (alive) setOwned(false);
      } finally {
        if (alive) setChecking(false);
      }
    };

    check();
    return () => { alive = false; };
  }, [open, id, context, accessToken]);

  // Wishlist check
  useEffect(() => {
    let alive = true;
    if (!open || !id || !accessToken || context !== "coverMarket") return;

    const checkWishlist = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/cover-wishlist/check/${id}`, {
          credentials: "omit",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json().catch(() => ({}));
        if (alive) setInWishlist(!!data.inWishlist);
      } catch {
        if (alive) setInWishlist(false);
      }
    };

    checkWishlist();
    return () => { alive = false; };
  }, [open, id, accessToken, context]);

  // Load drafts when showing assign panel
  useEffect(() => {
    if (!showAssign || !accessToken) return;
    let alive = true;

    const loadDrafts = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/drafts?type=manuscript`, {
          credentials: "omit",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json().catch(() => []);
        if (alive) setDrafts(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setDrafts([]);
      }
    };

    loadDrafts();
    return () => { alive = false; };
  }, [showAssign, accessToken]);

  // Primary action: buy or view assigned
  const handlePrimary = async () => {
    if (!cover || !id) return;

    if (owned) {
      setShowAssign(true);
      return;
    }

    if (!accessToken) {
      alert("Please sign in to purchase this cover.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/cover-market/checkout`, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ coverId: id }),
      });
      const data = await res.json();
      if (data?.alreadyOwned) {
        setOwned(true);
        return;
      }
      if (!res.ok || !data?.url) throw new Error("checkout failed");
      window.location.assign(data.url);
    } catch {
      alert("Could not start checkout. Please try again.");
    }
  };

  const handleAssign = async (draftId) => {
    if (!accessToken || !id || assigning) return;
    setAssigning(true);
    try {
      const res = await fetch(`${API_BASE}/api/cover-market/assign/${id}`, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ draftId }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Cover assigned to your draft!");
        setShowAssign(false);
      } else {
        alert(data?.error || "Failed to assign cover.");
      }
    } catch {
      alert("Could not assign cover. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  const handleWishlist = async () => {
    if (!accessToken) {
      alert("Please sign in to add covers to your wishlist.");
      return;
    }
    if (!id) return;
    setWishlistLoading(true);
    try {
      if (inWishlist) {
        const res = await fetch(`${API_BASE}/api/cover-wishlist/${id}`, {
          method: "DELETE",
          credentials: "omit",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) setInWishlist(false);
        else throw new Error("Failed to remove from wishlist");
      } else {
        const res = await fetch(`${API_BASE}/api/cover-wishlist/${id}`, {
          method: "POST",
          credentials: "omit",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        if (data?.alreadyInWishlist || res.ok) setInWishlist(true);
        else if (data?.error === "already_owned") alert("You already own this cover!");
        else throw new Error("Failed to add to wishlist");
      }
    } catch (error) {
      alert(`Could not update wishlist. ${error.message}`);
    } finally {
      setWishlistLoading(false);
    }
  };

  const computedLabel = checking
    ? "Checking…"
    : owned
    ? "Assign to Draft"
    : `Buy ${formatPrice(cover?.price_cents, cover?.currency)}`;

  if (!open || !cover) return null;

  const description = (cover.description || "").trim() || "No description provided.";

  return (
    <div
      className="ccm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="ccm-modal" role="dialog" aria-modal="true">
        <button className="ccm-closeX" aria-label="Close" onClick={onClose}>
          ×
        </button>

        <div className="ccm-header">
          <h2 className="ccm-title">{cover.title}</h2>
          <div className="ccm-designer">
            by {cover.designerName || "Unknown"}
          </div>
          {cover.coverType && <span className="ccm-chip">{cover.coverType}</span>}
          {cover.exclusive && <span className="ccm-chip ccm-chip--exclusive">Exclusive</span>}
        </div>

        <div className="ccm-body">
          {/* Cover preview */}
          <div className="ccm-previewBlock">
            <img
              className="ccm-previewImg"
              src={
                (() => {
                  const v = (cover.cover_url || cover.coverImage || '').trim();
                  if (!v) return `${API_BASE}/static/images/book-covers/notFound.jpg`;
                  if (v.startsWith('http')) return v;
                  if (v.startsWith('/')) return `${API_BASE}${v}`;
                  return `${API_BASE}/static/images/book-covers/${v}`;
                })()
              }
              alt={cover.title}
            />
          </div>

          {/* Description */}
          <div className="ccm-descBlock">
            <div className="ccm-descLabel">Description</div>
            <div>{description}</div>
          </div>

          {/* Tags */}
          {cover.tags && cover.tags.length > 0 && (
            <div className="ccm-tagsBlock">
              {cover.tags.map((tag) => (
                <span key={tag} className="ccm-tag">{tag}</span>
              ))}
            </div>
          )}

          {/* Price */}
          <div className="ccm-priceBlock">
            <span className="ccm-priceLabel">Price:</span>
            <span className="ccm-priceValue">
              {formatPrice(cover.price_cents, cover.currency)}
            </span>
          </div>

          {/* Assign to draft panel */}
          {showAssign && owned && (
            <div className="ccm-assignBlock">
              <div className="ccm-descLabel">Assign to one of your drafts</div>
              {drafts.length === 0 ? (
                <div style={{ color: '#666', fontSize: '14px' }}>
                  No manuscript drafts found. Create a draft first.
                </div>
              ) : (
                <div className="ccm-draftList">
                  {drafts.map((d) => (
                    <button
                      key={d._id}
                      className="ccm-draftBtn"
                      onClick={() => handleAssign(d._id)}
                      disabled={assigning}
                    >
                      {d.title || "Untitled"} {d.genre ? `(${d.genre})` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ccm-footer">
          <button className="ccm-btn" onClick={onClose}>
            Close
          </button>
          {!owned && context === "coverMarket" && (
            <button
              className="ccm-btnWishlist"
              onClick={handleWishlist}
              disabled={wishlistLoading}
            >
              {wishlistLoading
                ? "..."
                : inWishlist
                ? "Remove from Wishlist"
                : "Add to Wishlist"}
            </button>
          )}
          {!cover.soldOut && (
            <button
              className="ccm-btnPrimary"
              onClick={handlePrimary}
              disabled={checking}
            >
              {computedLabel}
            </button>
          )}
          {cover.soldOut && (
            <span className="ccm-soldOut">Sold Out</span>
          )}
        </div>
      </div>
    </div>
  );
}
